import { RuleEventIcon, RuleViewIcon } from '@/lib/Icons';
import { ExpressionCondition } from '@/models/audiences';
import { format } from 'date-fns';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '../../../../components/inputs';
import { EventSelect, SearchDropdown } from '../selects';
import { DatePickerInput } from '../selects/DatePickerInput';

type ActionConditionProps = {
  expression: ExpressionCondition;
  onChange?: (condition: ExpressionCondition) => void;
  isViewMode?: boolean;
};

export const ActionCondition: FC<ActionConditionProps> = ({ expression, isViewMode, onChange }) => {
  const { t } = useTranslation('audience_edit');
  const { t: tAudience } = useTranslation('audience_details');

  const comparisonOp: { id: string; displayName: string }[] = [
    {
      id: 'AT_LEAST',
      displayName: t('rules.at_least'),
    },
    {
      id: 'EXACTLY',
      displayName: t('rules.exactly'),
    },
    {
      id: 'AT_MOST',
      displayName: t('rules.at_most'),
    },
    {
      id: 'NEVER',
      displayName: t('rules.no_times'),
    },
  ];

  const temporalHorizonOp: { id: string; displayName: string }[] = [
    {
      id: 'WITHIN',
      displayName: t('rules.within'),
    },
    {
      id: 'BETWEEN',
      displayName: t('rules.in_between'),
    },
  ];

  const temporalHorizonUnits: { id: string; displayName: string }[] = [
    {
      id: 'DAY',
      displayName: t('rules.days'),
    },
    {
      id: 'MONTH',
      displayName: t('rules.months'),
    },
    {
      id: 'YEAR',
      displayName: t('rules.years'),
    },
  ];

  const [comparisonSearchQuery, setComparisonSearchQuery] = useState('');
  const [temporalHorizonSearchQuery, setTemporalHorizonSearchQuery] = useState('');
  const [temporalHorizonUnitsSearchQuery, setTemporalHorizonUnitsSearchQuery] = useState('');

  if (isViewMode) {
    return (
      <div className='flex flex-wrap items-center gap-2 text-sm'>
        {expression.kind === 'EVENT' ? (
          <span className='flex items-center gap-2'>
            <RuleEventIcon className='size-4 text-brand-600' />
            <span>{tAudience('sources_and_rules.rules.performedAnEvent_read_view')}</span>
          </span>
        ) : (
          <span className='flex items-center gap-2'>
            <RuleViewIcon className='size-4 text-brand-600' />
            <span>{tAudience('sources_and_rules.rules.haveViewedAPage_read_view')}</span>
          </span>
        )}

        <EventSelect
          ruleId={expression.dataSourceRuleId}
          type={expression.kind === 'EVENT' ? 'TRACK' : 'VIEW'}
          onChange={() => {}}
          isViewMode={true}
          className='rounded-md border border-quaternary-200 px-2 py-1 font-medium'
        />

        {expression.occurrences?.comparisonOp !== 'NEVER' && (
          <>
            <span>{comparisonOp.find((op) => op.id === expression.occurrences?.comparisonOp)?.displayName}</span>
            <span className='rounded-md border border-quaternary-200 px-2 py-1 font-medium'>{expression.occurrences?.times}</span>
            <span>{t('rules.times')}</span>
          </>
        )}

        {expression.occurrences?.comparisonOp === 'NEVER' && (
          <span>{comparisonOp.find((op) => op.id === expression.occurrences?.comparisonOp)?.displayName}</span>
        )}

        {expression.occurrences?.comparisonOp !== 'NEVER' && expression.temporalHorizon?.type === 'WITHIN' && (
          <>
            <span>{temporalHorizonOp.find((op) => op.id === expression.temporalHorizon?.type)?.displayName}</span>
            <span className='rounded-md border border-quaternary-200 px-2 py-1 font-medium'>{expression.temporalHorizon?.value}</span>
            <span>{temporalHorizonUnits.find((op) => op.id === expression.temporalHorizon?.unit)?.displayName}</span>
          </>
        )}

        {expression.occurrences?.comparisonOp !== 'NEVER' && expression.temporalHorizon?.type === 'BETWEEN' && (
          <>
            <span>{temporalHorizonOp.find((op) => op.id === expression.temporalHorizon?.type)?.displayName}</span>
            <span className='rounded-md border border-quaternary-200 px-2 py-1 font-medium'>{expression.temporalHorizon?.from}</span>
            <span>{t('rules.and')}</span>
            <span className='rounded-md border border-quaternary-200 px-2 py-1 font-medium'>{expression.temporalHorizon?.to}</span>
          </>
        )}
      </div>
    );
  }

  return (
    <div className='flex w-full items-start'>
      <div className='flex w-full items-start'>
        {/* Icon and text - Fixed width section */}
        <div className='mr-4 mt-2 min-w-[180px] shrink-0 whitespace-nowrap text-sm'>
          {expression.kind === 'EVENT' ? (
            <span className='font-medium'>
              <RuleEventIcon className='mr-2 inline-block size-6 text-brand-600' /> {t('rules.have_performed')}
            </span>
          ) : (
            <span className='font-medium'>
              <RuleViewIcon className='mr-2 inline-block size-6 text-brand-600' /> {t('rules.have_visited')}
            </span>
          )}
        </div>

        {/* 3x2 grid with Fields */}
        <div className='grid flex-1 grid-cols-1 items-start gap-3 md:grid-cols-3'>
          {/* Event Select */}
          <div className='md:col-span-1'>
            <EventSelect
              ruleId={expression.dataSourceRuleId}
              type={expression.kind === 'EVENT' ? 'TRACK' : 'VIEW'}
              onChange={(ruleItemValue) => {
                onChange?.({
                  ...expression,
                  dataSourceRuleId: String(ruleItemValue.id),
                });
              }}
              isViewMode={isViewMode}
            />
          </div>

          {/* Comparison Operator (At Least, Exactly, etc.) */}
          <div className='md:col-span-1'>
            <SearchDropdown
              items={
                comparisonSearchQuery?.trim()
                  ? comparisonOp.filter((item) => item.displayName.toLowerCase().includes(comparisonSearchQuery?.trim().toLowerCase()))
                  : comparisonOp
              }
              item={comparisonOp.find((op) => op.id === expression.occurrences?.comparisonOp)}
              setItem={(opItemValue) => {
                onChange?.({
                  ...expression,
                  occurrences: {
                    ...expression.occurrences,
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    comparisonOp: String(opItemValue.id),
                  },
                });
              }}
              setQuery={setComparisonSearchQuery}
              loading={false}
              readOnly={isViewMode}
              openWithFocus={true}
              className='px-2 py-1'
            >
              {(item) => <div className='whitespace-nowrap'>{item.displayName}</div>}
            </SearchDropdown>
          </div>

          {/* Times Input */}
          {expression.occurrences?.comparisonOp !== 'NEVER' && (
            <div className='md:col-span-1'>
              <Input
                name='times'
                id='times'
                type='number'
                className='inline-flex h-[45px] w-full items-center justify-center rounded-lg px-2 py-1 text-primary-900 focus:border-brand-600 focus:opacity-100 disabled:opacity-40'
                value={expression.occurrences?.times}
                onKeyPress={(event) => {
                  if (!/[0-9]/.test(event.key)) {
                    event.preventDefault();
                  }
                }}
                onChange={(ev) => {
                  onChange?.({
                    ...expression,
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    occurrences: {
                      ...expression.occurrences,
                      times: parseInt(ev.target.value.replace(/\D/, '')),
                    },
                  });
                }}
                isViewMode={isViewMode}
                isTimes={true}
              />
            </div>
          )}

          {/* Temporal Horizon Type (Within/Between) */}
          {expression.occurrences?.comparisonOp !== 'NEVER' && (
            <div className='md:col-span-1'>
              <SearchDropdown
                items={
                  temporalHorizonSearchQuery?.trim()
                    ? temporalHorizonOp.filter((item) => item.displayName.toLowerCase().includes(temporalHorizonSearchQuery?.trim().toLowerCase()))
                    : temporalHorizonOp
                }
                item={temporalHorizonOp.find((op) => op.id === expression.temporalHorizon?.type)}
                setItem={(opItemValue) => {
                  onChange?.({
                    ...expression,
                    temporalHorizon: {
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      type: String(opItemValue.id),
                    },
                  });
                }}
                setQuery={setTemporalHorizonSearchQuery}
                loading={false}
                readOnly={isViewMode}
                openWithFocus={true}
                className='px-2 py-1'
              >
                {(item) => <div className='whitespace-nowrap'>{item.displayName}</div>}
              </SearchDropdown>
            </div>
          )}

          {/* Within - Value and Unit */}
          {expression.occurrences?.comparisonOp !== 'NEVER' && expression.temporalHorizon?.type === 'WITHIN' && (
            <>
              <div className='md:col-span-1'>
                <Input
                  name='value'
                  id='value'
                  type='number'
                  className='h-[45px] w-full px-2 py-1'
                  value={expression.temporalHorizon?.value}
                  onKeyPress={(event) => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                  onChange={(ev) => {
                    onChange?.({
                      ...expression,
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      temporalHorizon: {
                        ...expression.temporalHorizon,
                        value: parseInt(ev.target.value.replace(/\D/, '')),
                      },
                    });
                  }}
                  isViewMode={isViewMode}
                />
              </div>
              <div className='md:col-span-1'>
                <SearchDropdown
                  items={
                    temporalHorizonUnitsSearchQuery?.trim()
                      ? temporalHorizonUnits.filter((item) => item.displayName.toLowerCase().includes(temporalHorizonUnitsSearchQuery?.trim().toLowerCase()))
                      : temporalHorizonUnits
                  }
                  item={temporalHorizonUnits.find((op) => op.id === expression.temporalHorizon?.unit)}
                  setItem={(opItemValue) => {
                    onChange?.({
                      ...expression,
                      temporalHorizon: {
                        ...expression.temporalHorizon,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        unit: String(opItemValue.id),
                      },
                    });
                  }}
                  setQuery={setTemporalHorizonUnitsSearchQuery}
                  loading={false}
                  readOnly={isViewMode}
                  openWithFocus={true}
                  className='px-2 py-1'
                >
                  {(item) => <div className='whitespace-nowrap'>{item.displayName}</div>}
                </SearchDropdown>
              </div>
            </>
          )}

          {/* Between - Date Range */}
          {expression.occurrences?.comparisonOp !== 'NEVER' && expression.temporalHorizon?.type === 'BETWEEN' && (
            <>
              <div className='md:col-span-1'>
                <DatePickerInput
                  selected={expression.temporalHorizon?.from ? new Date(expression.temporalHorizon?.from) : undefined}
                  dateFormat='P'
                  onChange={(date) => {
                    if (date instanceof Date) {
                      onChange?.({
                        ...expression,
                        temporalHorizon: {
                          ...expression.temporalHorizon,
                          from: format(date, 'yyyy-MM-dd'),
                        },
                      });
                    }
                  }}
                  isViewMode={isViewMode}
                  className='h-[45px] w-full px-2 py-1'
                />
              </div>
              <div className='md:col-span-1'>
                <DatePickerInput
                  selected={expression.temporalHorizon?.to ? new Date(expression.temporalHorizon?.to) : undefined}
                  dateFormat='P'
                  onChange={(date) => {
                    if (date instanceof Date) {
                      onChange?.({
                        ...expression,
                        temporalHorizon: {
                          ...expression.temporalHorizon,
                          to: format(date, 'yyyy-MM-dd'),
                        },
                      });
                    }
                  }}
                  isViewMode={isViewMode}
                  className='h-[45px] w-full px-2 py-1'
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
